import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import ContactActivitySheet from "@/components/contacts/ContactActivitySheet";
import { ArrowUpRight, Check, CheckCircle2, Clock, MessageSquare, Phone } from "lucide-react";
import { useCallDialog } from "@/context/CallDialogContext";
import { useMessageDialog } from "@/context/MessageDialogContext";
import { useGetCallsQuery, useUpdateTaskStatusMutation } from "@/store/rest_api";
import { getInitialsFromFullName } from "@/util/string";
import { daysSince, formatDate } from "@/util/date";
import { cn } from "@/lib/utils";
import type { AttentionTask, NeedsAttention, NeglectedLead } from "@/model/interface/DashboardSummary";
import type { CallStats } from "@/model/interface/Call";

type TabKey = "overdue" | "today" | "stale";

const attentionToneClasses: Record<"destructive" | "warning" | "info", string> = {
  destructive: "bg-destructive",
  warning: "bg-warning",
  info: "bg-info",
};

const AttentionRow: React.FC<{
  tone: "destructive" | "warning" | "info";
  title: string;
  meta?: string;
  actionLabel: string;
  onAction: () => void;
}> = ({ tone, title, meta, actionLabel, onAction }) => (
  <div className="flex items-center gap-3 rounded-md px-2 py-2">
    <span className={cn("h-2 w-2 shrink-0 rounded-full", attentionToneClasses[tone])} />
    <div className="min-w-0 flex-1">
      <span className="block truncate text-sm font-medium">{title}</span>
      {meta && <p className="truncate text-xs text-muted-foreground">{meta}</p>}
    </div>
    <Button variant="outline" size="sm" className="h-7 shrink-0" onClick={onAction}>
      {actionLabel}
    </Button>
  </div>
);

const SinceBadge: React.FC<{ lastContactedAt?: string }> = ({ lastContactedAt }) => {
  const days = daysSince(lastContactedAt);
  const label = days === null ? "Never contacted" : `${days}d since contact`;
  const tone =
    days === null || days >= 30
      ? "border-destructive/40 bg-destructive/5 text-destructive"
      : days >= 14
        ? "border-warning/40 bg-warning-soft text-warning-soft-foreground"
        : "text-muted-foreground";
  return (
    <Badge
      variant="outline"
      className={`gap-1 whitespace-nowrap ${tone}`}
      title={lastContactedAt ? `Last contacted ${formatDate(lastContactedAt)}` : "No recorded contact"}
    >
      <Clock className="h-3 w-3" />
      {label}
    </Badge>
  );
};

const DueBadge: React.FC<{ dueDate?: string; overdue: boolean }> = ({ dueDate, overdue }) => {
  if (overdue) {
    const days = daysSince(dueDate) ?? 0;
    return (
      <Badge variant="outline" className="whitespace-nowrap border-destructive/40 bg-destructive/5 text-destructive">
        {days}d overdue
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="whitespace-nowrap border-warning/40 bg-warning-soft text-warning-soft-foreground">
      Due today
    </Badge>
  );
};

const IconAction: React.FC<{ label: string; onClick: () => void; children: React.ReactNode }> = ({
  label,
  onClick,
  children,
}) => (
  <Button
    type="button"
    variant="ghost"
    size="icon"
    className="h-7 w-7 text-muted-foreground hover:text-foreground"
    title={label}
    aria-label={label}
    onClick={(event) => {
      event.stopPropagation();
      onClick();
    }}
  >
    {children}
  </Button>
);

const ActionCluster: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex items-center gap-0.5 opacity-100 transition-opacity sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100">
    {children}
  </div>
);

const TaskRow: React.FC<{
  task: AttentionTask;
  onCall: (phone?: string) => void;
  onText: (phone?: string) => void;
  onComplete: () => void;
  onView: () => void;
  onOpenContact: (contactId: string) => void;
}> = ({ task, onCall, onText, onComplete, onView, onOpenContact }) => (
  <div
    role="button"
    tabIndex={0}
    onClick={onView}
    className="group flex cursor-pointer items-center gap-3 rounded-md px-2 py-2.5 hover:bg-muted/50"
  >
    <Avatar className="h-9 w-9 shrink-0">
      <AvatarFallback className="bg-muted text-xs">{getInitialsFromFullName(task.contactName ?? task.title)}</AvatarFallback>
    </Avatar>
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2">
        <span className="truncate text-sm font-medium">{task.title}</span>
        {task.priority === "HIGH" && (
          <Badge variant="outline" className="shrink-0 border-destructive/40 px-1 py-0 text-[10px] text-destructive">
            High
          </Badge>
        )}
      </div>
      <p className="truncate text-xs text-muted-foreground">
        {task.contactName && task.contactId ? (
          <button
            className="hover:underline"
            onClick={(event) => {
              event.stopPropagation();
              onOpenContact(task.contactId!);
            }}
          >
            {task.contactName}
          </button>
        ) : (
          task.contactName || "No linked contact"
        )}
        {task.ownerName && <span> · {task.ownerName}</span>}
      </p>
    </div>
    <div className="flex shrink-0 items-center gap-2">
      <DueBadge dueDate={task.dueDate} overdue={task.overdue} />
      <ActionCluster>
        {task.phone && (
          <IconAction label="Call" onClick={() => onCall(task.phone)}>
            <Phone className="h-3.5 w-3.5" />
          </IconAction>
        )}
        {task.phone && (
          <IconAction label="Text" onClick={() => onText(task.phone)}>
            <MessageSquare className="h-3.5 w-3.5" />
          </IconAction>
        )}
        <IconAction label="Mark done" onClick={onComplete}>
          <Check className="h-3.5 w-3.5" />
        </IconAction>
        <IconAction label="Open" onClick={onView}>
          <ArrowUpRight className="h-3.5 w-3.5" />
        </IconAction>
      </ActionCluster>
    </div>
  </div>
);

const LeadRow: React.FC<{
  lead: NeglectedLead;
  onCall: (phone?: string) => void;
  onText: (phone?: string) => void;
  onView: () => void;
}> = ({ lead, onCall, onText, onView }) => (
  <div
    role="button"
    tabIndex={0}
    onClick={onView}
    className="group flex cursor-pointer items-center gap-3 rounded-md px-2 py-2.5 hover:bg-muted/50"
  >
    <Avatar className="h-9 w-9 shrink-0">
      <AvatarFallback className="bg-muted text-xs">{getInitialsFromFullName(lead.name)}</AvatarFallback>
    </Avatar>
    <div className="min-w-0 flex-1">
      <span className="block truncate text-sm font-medium">{lead.name}</span>
      <p className="truncate text-xs text-muted-foreground">
        {[lead.phone, lead.ownerName].filter(Boolean).join(" · ") || "No phone"}
      </p>
    </div>
    <div className="flex shrink-0 items-center gap-2">
      <SinceBadge lastContactedAt={lead.lastContactedAt} />
      <ActionCluster>
        {lead.phone && (
          <IconAction label="Call" onClick={() => onCall(lead.phone)}>
            <Phone className="h-3.5 w-3.5" />
          </IconAction>
        )}
        {lead.phone && (
          <IconAction label="Text" onClick={() => onText(lead.phone)}>
            <MessageSquare className="h-3.5 w-3.5" />
          </IconAction>
        )}
        <IconAction label="Open" onClick={onView}>
          <ArrowUpRight className="h-3.5 w-3.5" />
        </IconAction>
      </ActionCluster>
    </div>
  </div>
);

const EmptyBucket: React.FC<{ message: string }> = ({ message }) => (
  <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
    <CheckCircle2 className="h-8 w-8 text-success" />
    <p className="text-sm text-muted-foreground">{message}</p>
  </div>
);

const NeedsAttentionCard: React.FC<{ needsAttention?: NeedsAttention; callStats?: CallStats; from?: string }> = ({
  needsAttention,
  callStats,
  from,
}) => {
  const navigate = useNavigate();
  const { setPendingDialNumber, openCallDialog } = useCallDialog();
  const { setPendingRecipient, openMessageDialog } = useMessageDialog();
  const [updateTaskStatus] = useUpdateTaskStatusMutation();
  const [hiddenTaskIds, setHiddenTaskIds] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<TabKey | null>(null);
  const [panelContactId, setPanelContactId] = useState<string | null>(null);
  const { data: missedCallsData } = useGetCallsQuery(
    { missedWithoutCallback: true, size: 3, from },
    { skip: (callStats?.missedWithoutCallback ?? 0) === 0 },
  );

  const call = (phone?: string) => {
    if (!phone) {
      return;
    }
    setPendingDialNumber(phone);
    openCallDialog();
  };

  const text = (phone?: string) => {
    if (!phone) {
      return;
    }
    setPendingRecipient(phone);
    openMessageDialog();
  };

  const openTask = (task: AttentionTask) => {
    if (task.id) {
      navigate(`/tasks?task=${task.id}`);
    } else {
      navigate("/tasks");
    }
  };

  const completeTask = async (task: AttentionTask) => {
    if (!task.id) {
      return;
    }
    const id = task.id;
    setHiddenTaskIds((previous) => new Set(previous).add(id));
    try {
      await updateTaskStatus({ id, status: "DONE" }).unwrap();
      toast.success("Task marked done");
    } catch {
      setHiddenTaskIds((previous) => {
        const next = new Set(previous);
        next.delete(id);
        return next;
      });
      toast.error("Could not complete task");
    }
  };

  useEffect(() => {
    if (!needsAttention || activeTab) {
      return;
    }
    if (needsAttention.overdueCount > 0) {
      setActiveTab("overdue");
    } else if (needsAttention.dueTodayCount > 0) {
      setActiveTab("today");
    } else {
      setActiveTab("stale");
    }
  }, [needsAttention, activeTab]);

  if (!needsAttention) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Needs attention</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton key={index} className="h-12 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  const overdueTasks = needsAttention.overdueTasks.filter((task) => !task.id || !hiddenTaskIds.has(task.id));
  const dueTodayTasks = needsAttention.dueTodayTasks.filter((task) => !task.id || !hiddenTaskIds.has(task.id));
  const neglectedLeads = needsAttention.neglectedLeads;
  const hiddenOverdue = needsAttention.overdueTasks.length - overdueTasks.length;
  const hiddenToday = needsAttention.dueTodayTasks.length - dueTodayTasks.length;
  const overdueCount = Math.max(0, needsAttention.overdueCount - hiddenOverdue);
  const dueTodayCount = Math.max(0, needsAttention.dueTodayCount - hiddenToday);
  const staleCount = needsAttention.neglectedLeadCount;
  const missedCalls = missedCallsData?.items ?? [];
  const missedWithoutCallbackCount = callStats?.missedWithoutCallback ?? 0;
  const noDispositionCount = callStats?.noDisposition ?? 0;
  const unreviewedRecordingsCount = callStats?.unreviewedRecordings ?? 0;
  const callAttentionTotal = missedWithoutCallbackCount + noDispositionCount + unreviewedRecordingsCount;
  const taskLeadTotal = overdueCount + dueTodayCount + staleCount;
  const total = taskLeadTotal + callAttentionTotal;

  if (total === 0) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Needs attention</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
            <CheckCircle2 className="h-10 w-10 text-success" />
            <p className="text-sm font-medium">Nothing needs your attention right now</p>
            <p className="text-xs text-muted-foreground">Missed calls, overdue tasks and neglected leads will show up here.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-base">Needs attention</CardTitle>
        <span className="text-xs text-muted-foreground">
          {total} item{total !== 1 ? "s" : ""}
        </span>
      </CardHeader>
      <CardContent>
        {callAttentionTotal > 0 && (
          <div className={cn("flex flex-col gap-0.5", taskLeadTotal > 0 && "mb-3 border-b pb-3")}>
            {missedCalls.map((missedCall) => (
              <AttentionRow
                key={missedCall.id}
                tone="destructive"
                title={
                  [missedCall.contact?.firstName, missedCall.contact?.lastName].filter(Boolean).join(" ") ||
                  missedCall.phoneNumber
                }
                meta={
                  missedCall.createdAt
                    ? `Missed call · ${formatDistanceToNow(new Date(missedCall.createdAt), { addSuffix: true })}`
                    : "Missed call"
                }
                actionLabel="Call back"
                onAction={() => call(missedCall.phoneNumber)}
              />
            ))}
            {missedWithoutCallbackCount > missedCalls.length && (
              <Button
                variant="link"
                size="sm"
                className="h-auto justify-start px-2"
                onClick={() => navigate("/calls?attention=missedWithoutCallback")}
              >
                View all {missedWithoutCallbackCount} missed calls without callback
              </Button>
            )}
            {noDispositionCount > 0 && (
              <AttentionRow
                tone="warning"
                title={`${noDispositionCount} ${noDispositionCount === 1 ? "call" : "calls"} without disposition`}
                actionLabel="Review"
                onAction={() => navigate("/calls?attention=noDisposition")}
              />
            )}
            {unreviewedRecordingsCount > 0 && (
              <AttentionRow
                tone="info"
                title={`${unreviewedRecordingsCount} unreviewed ${unreviewedRecordingsCount === 1 ? "recording" : "recordings"}`}
                actionLabel="Listen"
                onAction={() => navigate("/calls?attention=unreviewedRecording")}
              />
            )}
          </div>
        )}
        {taskLeadTotal > 0 && (
        <Tabs value={activeTab ?? "overdue"} onValueChange={(value) => setActiveTab(value as TabKey)}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="overdue" className="gap-1.5">
              Overdue
              {overdueCount > 0 && (
                <Badge variant="destructive" className="h-5 px-1.5 text-[11px]">
                  {overdueCount}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="today" className="gap-1.5">
              Today
              {dueTodayCount > 0 && (
                <Badge variant="secondary" className="h-5 px-1.5 text-[11px]">
                  {dueTodayCount}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="stale" className="gap-1.5">
              Stale leads
              {staleCount > 0 && (
                <Badge variant="secondary" className="h-5 px-1.5 text-[11px]">
                  {staleCount}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overdue" className="mt-3">
            {overdueTasks.length === 0 ? (
              <EmptyBucket message="No overdue tasks." />
            ) : (
              <div className="divide-y">
                {overdueTasks.map((task) => (
                  <TaskRow
                    key={task.id}
                    task={task}
                    onCall={call}
                    onText={text}
                    onComplete={() => completeTask(task)}
                    onView={() => openTask(task)}
                    onOpenContact={setPanelContactId}
                  />
                ))}
              </div>
            )}
            {overdueCount > overdueTasks.length && (
              <Button variant="link" size="sm" className="mt-1 h-auto px-2" onClick={() => navigate("/tasks")}>
                View all {overdueCount} overdue
              </Button>
            )}
          </TabsContent>

          <TabsContent value="today" className="mt-3">
            {dueTodayTasks.length === 0 ? (
              <EmptyBucket message="Nothing due today." />
            ) : (
              <div className="divide-y">
                {dueTodayTasks.map((task) => (
                  <TaskRow
                    key={task.id}
                    task={task}
                    onCall={call}
                    onText={text}
                    onComplete={() => completeTask(task)}
                    onView={() => openTask(task)}
                    onOpenContact={setPanelContactId}
                  />
                ))}
              </div>
            )}
            {dueTodayCount > dueTodayTasks.length && (
              <Button variant="link" size="sm" className="mt-1 h-auto px-2" onClick={() => navigate("/tasks")}>
                View all {dueTodayCount} due today
              </Button>
            )}
          </TabsContent>

          <TabsContent value="stale" className="mt-3">
            {neglectedLeads.length === 0 ? (
              <EmptyBucket message="No neglected leads." />
            ) : (
              <div className="divide-y">
                {neglectedLeads.map((lead) => (
                  <LeadRow
                    key={lead.id}
                    lead={lead}
                    onCall={call}
                    onText={text}
                    onView={() => lead.id && setPanelContactId(lead.id)}
                  />
                ))}
              </div>
            )}
            {staleCount > neglectedLeads.length && (
              <Button variant="link" size="sm" className="mt-1 h-auto px-2" onClick={() => navigate("/leads")}>
                View all {staleCount} stale leads
              </Button>
            )}
          </TabsContent>
        </Tabs>
        )}
      </CardContent>
    </Card>
    <ContactActivitySheet
      contactId={panelContactId}
      onOpenChange={(open) => { if (!open) { setPanelContactId(null); } }}
    />
    </>
  );
};

export default NeedsAttentionCard;
